import React from 'react';
import { Controller, useForm, useFormState } from 'react-hook-form';
import CreatableSelect from 'react-select/creatable';

import { Message } from '@components';
import {
  FieldWrapper,
  FormFieldset,
  SelectField,
  TextField,
} from '@Forms/fields.jsx';
import { errorsFromMutate } from '@Forms/utils.js';
import { ButtonSpinnerIcon } from '@svgs';

export const ProjectSettingsForm = ({ businessData, disabled, mutation }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setError,
    setFocus,
  } = useForm({
    defaultValues: businessData,
    mode: 'onBlur',
  });
  const { isDirty, isValid } = useFormState({ control });

  const onSubmit = (data) => {
    mutation.mutate(data, {
      onError: (error) => {
        // handle server-side validations and errors
        const errorsList = errorsFromMutate(error);
        errorsList.forEach(({ field, error }) => setError(field, error));
      },
    });
  };

  // list of common locales to choose from. But users can manually enter locales if valid
  let localeChoices = [
    { value: 'en-us', label: 'English-US (en-us)' },
    { value: 'es-us', label: 'Spanish-US (es-us)' },
    { value: 'en-ca', label: 'English-Canada (en-ca)' },
    { value: 'fr-ca', label: 'French-Canada (fr-ca)' },
  ];
  const standardLocales = localeChoices.map((l) => l.value);
  // create a list of additional locales not in the default
  const additionalLocales = businessData.available_locales
    .filter((l) => !standardLocales.includes(l))
    .map((l) => {
      return { value: l, label: l };
    });
  // updated locale choices with custom ones included
  localeChoices = [...localeChoices, ...additionalLocales];

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={
        `sb-manage-content-form ` + (disabled ? 'sb-form-disabled' : '')
      }
    >
      <FormFieldset>
        <TextField
          register={register}
          error={errors.business_name}
          options={{ required: 'This field is required' }}
          name={`business_name`}
          label="Project name"
        />
      </FormFieldset>
      <FormFieldset legend="Domain settings and defaults">
        <TextField
          register={register}
          error={errors.primary_domain}
          name={`primary_domain`}
          label="Primary domain"
          placeholder="example.com"
        />
        <FieldWrapper
          error={errors.available_locales}
          label="Available locales"
          name={'available_locales'}
          helpText={
            <>
              List of available locales for internationalization and translated
              content.
              <br />
              Custom options can be entered using a [language]-[region] format
              (i.e. en-uk)
            </>
          }
        >
          <Controller
            control={control}
            name={`available_locales`}
            render={({ field, value, name, ref }) => (
              <CreatableSelect
                name={field.name}
                inputRef={field.ref}
                isClearable
                isMulti
                className="basic-multi-select"
                classNamePrefix="select"
                options={localeChoices}
                onChange={(val) => field.onChange(val.map((v) => v.value))}
                defaultValue={
                  field.value
                    ? field.value.map((v) =>
                        localeChoices.find((c) => c.value === v),
                      )
                    : []
                }
              />
            )}
          />
        </FieldWrapper>
      </FormFieldset>
      <FormFieldset legend="Advanced">
        <SelectField
          register={register}
          error={errors.project_type}
          options={{ required: 'This field is required' }}
          name="project_type"
          label="Change project type"
          helpText="Use with caution"
        >
          <option value="">Choose a type...</option>
          <option value="default">Standard project</option>
          <option value="legacy">Legacy project</option>
        </SelectField>
      </FormFieldset>
      <div className="sb-manage-content-button-bar">
        <div className="sb-flex">
          {/*<button className="sb-btn-cancel sb-btn" onClick={resetForm}>Cancel</button>*/}
          <button
            type="submit"
            className="sb-btn sb-btn-save"
            disabled={disabled}
          >
            {mutation.isPending ? 'Saving...' : 'Save changes'}
            {mutation.isPending && <ButtonSpinnerIcon />}
          </button>
        </div>
        {!isValid && isDirty && (
          <Message messageStyle="error" title="Please review your form" />
        )}
        {mutation.isError && (
          <Message
            messageStyle="error"
            title="Error saving changes"
            message="Please review your form"
          />
        )}
        {mutation.isSuccess && (
          <Message messageStyle="success" title="Successfully saved" />
        )}
      </div>
    </form>
  );
};
